import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { notificationService, Notification } from '../../services/notificationService';
import { notificationMessage, notificationOrderLabel, notificationTimeAgo } from '../../utils/notificationText';
import BottomSheet from '../ui/BottomSheet';
import { navigateFromLayer } from '../backStack';

/**
 * The bell's list on a phone: the latest 20 notifications, unread ones marked, a tap opens the
 * order (and marks it read). Same wording as the desktop dropdown (utils/notificationText).
 */
interface Props {
  open: boolean;
  onClose: () => void;
  onRead: () => void;
}

const NotificationsSheet: React.FC<Props> = ({ open, onClose, onRead }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setFailed(false);
    notificationService
      .getNotifications(20)
      .then((list) => { if (alive) setItems(list); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [open]);

  const openOne = async (n: Notification) => {
    if (!n.read) {
      setItems((cur) => cur && cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      try {
        await notificationService.markAsRead(n.id);
        onRead();
      } catch {
        /* the order still opens; the dot comes back on the next refresh */
      }
    }
    onClose();
    navigateFromLayer(navigate, `/orders/${n.orderId}`);
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={String(t('notifications.title'))}>
      {items === null && !failed && (
        <div className="m-skel-list" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className="m-skel m-skel-row" />)}
        </div>
      )}
      {failed && <p className="m-empty-line">{String(t('mobile.loadFailed', 'تعذّر التحميل، حاول مرة أخرى'))}</p>}
      {items && items.length === 0 && <p className="m-empty-line">{String(t('notifications.noNotifications'))}</p>}
      {items && items.length > 0 && (
        <ul className="m-notes">
          {items.map((n) => (
            <li key={n.id}>
              <button type="button" className={`m-note${n.read ? '' : ' is-unread'}`} onClick={() => openOne(n)}>
                <span className="m-note-dot" aria-hidden="true" />
                <span className="m-note-body">
                  <span className="m-note-msg">{notificationMessage(n, t)}</span>
                  <span className="m-note-meta">
                    <span dir="auto">{notificationOrderLabel(n, user?.role)}</span>
                    <span aria-hidden="true"> · </span>
                    <span>{notificationTimeAgo(n.createdAt, t)}</span>
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
};

export default NotificationsSheet;
